/**
 * Hook for managing context search functionality
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useCspApi } from "@perfectserve/http-ps";
export const useContextSearch = (isPerfectServeUser, permissionsLoaded) => {
    const { get } = useCspApi();
    // Use ref to store the get function to avoid dependency issues
    const getRef = useRef(get);
    getRef.current = get;
    // Simple in-memory state - no localStorage
    const [searchText, setSearchText] = useState("");
    const [accountResults, setAccountResults] = useState([]);
    const [userResults, setUserResults] = useState([]);
    const [privilegeGroupResults, setPrivilegeGroupResults] = useState([]);
    const [allPrivilegeGroups, setAllPrivilegeGroups] = useState([]);
    const [privilegeGroupsLoaded, setPrivilegeGroupsLoaded] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [activeTab, setActiveTab] = useState("accounts");
    // Load all privilege groups when first needed (lazy loading)
    const loadAllPrivilegeGroups = useCallback(async () => {
        if (privilegeGroupsLoaded)
            return;
        setIsSearching(true);
        setSearchError(null);
        try {
            const response = await getRef.current("/api/privilegeadmin/groups");
            const allGroups = response.data;
            const sortedGroups = Array.isArray(allGroups) ? allGroups.sort((a, b) => a.Name.localeCompare(b.Name)) : [];
            setAllPrivilegeGroups(sortedGroups);
            setPrivilegeGroupsLoaded(true);
        }
        catch (error) {
            setSearchError(error instanceof Error ? error.message : "Failed to load privilege groups");
        }
        finally {
            setIsSearching(false);
        }
    }, [privilegeGroupsLoaded]);
    // No persistence needed - keep everything in memory
    // Clear search results when switching tabs (but preserve search text for UX)
    useEffect(() => {
        setAccountResults([]);
        setUserResults([]);
        setPrivilegeGroupResults([]);
    }, [activeTab]);
    // Update privilege group results when data is loaded and filter based on search text
    useEffect(() => {
        if (activeTab === "privilegeGroups" && privilegeGroupsLoaded && searchText.trim().length >= 2) {
            const searchTerm = searchText.toLowerCase().trim();
            const filtered = allPrivilegeGroups
                .filter((group) => group.Name.toLowerCase().indexOf(searchTerm) !== -1)
                .sort((a, b) => a.Name.localeCompare(b.Name));
            setPrivilegeGroupResults(filtered);
        }
    }, [activeTab, privilegeGroupsLoaded, allPrivilegeGroups, searchText]);
    // Debounced search function
    const _debouncedSearch = useCallback(async (text) => {
        if (activeTab === "privilegeGroups") {
            if (!text.trim() || text.length < 2) {
                setPrivilegeGroupResults([]);
                return;
            }
            if (!privilegeGroupsLoaded) {
                await loadAllPrivilegeGroups();
            }
            return;
        }
        if (!text.trim() || text.length < 2) {
            setAccountResults([]);
            setUserResults([]);
            setPrivilegeGroupResults([]);
            return;
        }
        setIsSearching(true);
        setSearchError(null);
        try {
            if (activeTab === "accounts") {
                const response = await getRef.current(`/api/accounts?SearchText=${encodeURIComponent(text)}`);
                const results = response.data;
                const accountsArray = Array.isArray(results) ? results : results.data || results.results || [];
                const sortedAccounts = accountsArray.sort((a, b) => a.AccountName.localeCompare(b.AccountName));
                setAccountResults(sortedAccounts);
                setUserResults([]);
                setPrivilegeGroupResults([]);
            }
            else if (activeTab === "users") {
                const response = await getRef.current(`/api/usermanagement/users?searchtext=${encodeURIComponent(text)}`);
                const results = response.data;
                const usersArray = Array.isArray(results) ? results : results.data || results.results || [];
                const sortedUsers = usersArray.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
                setUserResults(sortedUsers);
                setAccountResults([]);
                setPrivilegeGroupResults([]);
            }
        }
        catch (error) {
            setSearchError(error instanceof Error ? error.message : "Search failed");
            setAccountResults([]);
            setUserResults([]);
            setPrivilegeGroupResults([]);
        }
        finally {
            setIsSearching(false);
        }
    }, [activeTab, allPrivilegeGroups, privilegeGroupsLoaded]);
    // Debounced search effect - ONLY triggers on searchText changes
    useEffect(() => {
        if (!searchText.trim()) {
            setAccountResults([]);
            setUserResults([]);
            setPrivilegeGroupResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            const text = searchText;
            if (!text.trim() || text.length < 2) {
                setAccountResults([]);
                setUserResults([]);
                setPrivilegeGroupResults([]);
                return;
            }
            // CRITICAL: For PerfectServe users on accounts/users tabs, wait for permissions to load
            // Non-PerfectServe users don't use search API for accounts, so they don't need to wait
            if (activeTab === "accounts" || activeTab === "users") {
                // If permissions haven't loaded yet, we can't determine if user is PerfectServe
                if (permissionsLoaded === false) {
                    return;
                }
                // If isPerfectServeUser is undefined, permissions haven't loaded yet
                if (isPerfectServeUser === undefined) {
                    return;
                }
                // Non-PerfectServe users don't use account search API (they use privileged accounts)
                if (!isPerfectServeUser && activeTab === "accounts") {
                    return;
                }
            }
            setIsSearching(true);
            setSearchError(null);
            try {
                if (activeTab === "accounts") {
                    const response = await getRef.current(`/api/accounts?SearchText=${encodeURIComponent(text)}`);
                    const results = response.data;
                    const accountsArray = Array.isArray(results) ? results : results.data || results.results || [];
                    const sortedAccounts = accountsArray.sort((a, b) => a.AccountName.localeCompare(b.AccountName));
                    setAccountResults(sortedAccounts);
                    setUserResults([]);
                    setPrivilegeGroupResults([]);
                }
                else if (activeTab === "users") {
                    const response = await getRef.current(`/api/usermanagement/users?searchtext=${encodeURIComponent(text)}`);
                    const results = response.data;
                    const usersArray = Array.isArray(results) ? results : results.data || results.results || [];
                    const sortedUsers = usersArray.sort((a, b) => a.DisplayName.localeCompare(b.DisplayName));
                    setUserResults(sortedUsers);
                    setAccountResults([]);
                    setPrivilegeGroupResults([]);
                }
                else if (activeTab === "privilegeGroups") {
                    if (!privilegeGroupsLoaded) {
                        await loadAllPrivilegeGroups();
                    }
                    const searchTerm = text.toLowerCase().trim();
                    const filtered = allPrivilegeGroups
                        .filter((group) => group.Name.toLowerCase().indexOf(searchTerm) !== -1)
                        .sort((a, b) => a.Name.localeCompare(b.Name));
                    setPrivilegeGroupResults(filtered);
                }
            }
            catch (error) {
                setSearchError(error instanceof Error ? error.message : "Search failed");
                setAccountResults([]);
                setUserResults([]);
                setPrivilegeGroupResults([]);
            }
            finally {
                setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText, activeTab, isPerfectServeUser, permissionsLoaded]); // Added dependencies to properly react to permission changes
    const clearResults = useCallback(() => {
        setSearchText("");
        setAccountResults([]);
        setUserResults([]);
        setPrivilegeGroupResults([]);
        setSearchError(null);
    }, []);
    // Function to load accounts from a privilege group
    const loadAccountsFromGroup = useCallback(async (groupId) => {
        // Get privileges for the group
        const privilegesResponse = await getRef.current(`/api/privilegeadmin/groups/${groupId}/privileges`);
        const privileges = privilegesResponse.data;
        // Extract unique AccountIds from privileges (Context.ObjectId that starts with "ACCT-")
        const accountIds = new Set();
        if (Array.isArray(privileges)) {
            privileges.forEach((privilege) => {
                // Extract ObjectId from Context if it's an account (starts with "ACCT-")
                if (privilege.Context?.ObjectId?.startsWith("ACCT-")) {
                    accountIds.add(privilege.Context.ObjectId);
                }
            });
        }
        // Fetch account details for each unique AccountId
        const accounts = [];
        for (const accountId of accountIds) {
            try {
                const accountResponse = await getRef.current(`/api/accounts/${accountId}`);
                const accountData = accountResponse.data;
                // Map API response to Account interface (API uses AccountId, we use Id)
                if (accountData && (accountData.Id || accountData.AccountId)) {
                    const mappedAccount = {
                        Id: accountData.Id || accountData.AccountId,
                        AccountName: accountData.AccountName,
                        AccountType: accountData.AccountType,
                        Msa: accountData.Msa,
                    };
                    accounts.push(mappedAccount);
                }
            }
            catch (_error) {
                // Silently handle individual account fetch errors
                // Continue with other accounts even if one fails
            }
        }
        return accounts.sort((a, b) => a.AccountName.localeCompare(b.AccountName));
    }, []);
    return {
        searchText,
        setSearchText,
        accountResults,
        userResults,
        privilegeGroupResults,
        allPrivilegeGroups,
        isSearching,
        searchError,
        activeTab,
        setActiveTab,
        clearResults,
        loadAccountsFromGroup,
    };
};
